import { ChangeDetectionStrategy, Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';

import { Notification, NotificationType } from './alert.const';
import { ToastService } from './toast.service';

const AUTO_DISMISS_MS = 5000;

const ICON_BY_TYPE: Readonly<Record<NotificationType, string>> = {
  [NotificationType.Success]: 'check_circle',
  [NotificationType.Error]: 'error',
  [NotificationType.Info]: 'info',
  [NotificationType.Warning]: 'warning'
};

const CLASS_BY_TYPE: Readonly<Record<NotificationType, string>> = {
  [NotificationType.Success]: 'toast--success',
  [NotificationType.Error]: 'toast--error',
  [NotificationType.Info]: 'toast--info',
  [NotificationType.Warning]: 'toast--warning'
};

@Component({
  selector: 'app-toast-alert',
  templateUrl: './toast-alert.component.html',
  styleUrl: './toast-alert.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatIconModule]
})
export class ToastAlertComponent {
  private readonly toastService = inject(ToastService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly notifications = signal<Notification[]>([]);

  constructor() {
    this.toastService
      .getAlert()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((alert: Notification | undefined) => {
        if (!alert) {
          this.notifications.set([]);
          return;
        }
        this.notifications.update((list) => [...list, alert]);
        setTimeout(() => this.dismiss(alert), AUTO_DISMISS_MS);
      });
  }

  protected dismiss(notification: Notification): void {
    this.notifications.update((list) => list.filter((n) => n !== notification));
  }

  protected iconFor(notification: Notification): string {
    return ICON_BY_TYPE[notification.type] ?? 'info';
  }

  protected classFor(notification: Notification): string {
    return CLASS_BY_TYPE[notification.type] ?? '';
  }
}
